<script setup>
import axios from 'axios'
import { reactive, ref } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import useNotification from '../../Services/useNotification'

const props = defineProps({
    fruitId: { type: Number, required: true },
    regionalPrices: { type: Array, default: () => [] },
    regionalComparisonEnabled: { type: Boolean, default: true },
    regionalComparisonGloballyEnabled: { type: Boolean, default: false },
})

const notifications = useNotification()
const saving = ref(false)
const savingVisibility = ref(false)
const comparisonEnabled = ref(props.regionalComparisonEnabled)

const departments = [
    ['Amazonas', -1.4429, -71.5724],
    ['Antioquia', 6.2442, -75.5812],
    ['Arauca', 7.0847, -70.7591],
    ['Atlántico', 10.9685, -74.7813],
    ['Bogotá D.C.', 4.7110, -74.0721],
    ['Bolívar', 10.3910, -75.4794],
    ['Boyacá', 5.4545, -73.3620],
    ['Caldas', 5.0703, -75.5138],
    ['Caquetá', 1.6144, -75.6062],
    ['Casanare', 5.3489, -72.4005],
    ['Cauca', 2.4448, -76.6147],
    ['Cesar', 10.4631, -73.2532],
    ['Chocó', 5.6919, -76.6583],
    ['Córdoba', 8.7500, -75.8814],
    ['Cundinamarca', 4.7110, -74.0721],
    ['Guainía', 3.8653, -67.9239],
    ['Guaviare', 2.5729, -72.6459],
    ['Huila', 2.9273, -75.2819],
    ['La Guajira', 11.5444, -72.9072],
    ['Magdalena', 11.2408, -74.1990],
    ['Meta', 4.1420, -73.6266],
    ['Nariño', 1.2136, -77.2811],
    ['Norte de Santander', 7.8891, -72.4967],
    ['Putumayo', 1.1528, -76.6513],
    ['Quindío', 4.5339, -75.6811],
    ['Risaralda', 4.8143, -75.6946],
    ['San Andrés y Providencia', 12.5847, -81.7006],
    ['Santander', 7.1193, -73.1227],
    ['Sucre', 9.3047, -75.3978],
    ['Tolima', 4.4389, -75.2322],
    ['Valle del Cauca', 3.4516, -76.5320],
    ['Vaupés', 1.2536, -70.2339],
    ['Vichada', 6.1847, -67.4932],
]

const units = [
    ['g', 'Gramo'], ['kg', 'Kilogramo'], ['lb', 'Libra'], ['arroba', 'Arroba'],
    ['quintal', 'Quintal'], ['ton', 'Tonelada métrica'], ['unidad', 'Unidad'],
    ['docena', 'Docena'], ['ciento', 'Ciento'], ['racimo', 'Racimo'],
    ['manojo', 'Manojo'], ['atado', 'Atado'], ['bandeja', 'Bandeja'],
    ['bolsa', 'Bolsa'], ['paquete', 'Paquete'], ['caja', 'Caja'],
    ['canastilla', 'Canastilla'], ['bulto', 'Bulto'], ['saco', 'Saco'],
    ['litro', 'Litro'], ['galon', 'Galón'],
]

const currencies = ['COP', 'USD', 'EUR', 'GBP', 'BRL', 'PEN', 'MXN', 'MYR']
const today = () => new Date().toISOString().slice(0, 10)

const initialForm = () => ({
    fruit_id: props.fruitId,
    country: 'Colombia',
    country_code: 'CO',
    region: '',
    municipality: '',
    latitude: '',
    longitude: '',
    price: '',
    currency: 'COP',
    unit: 'kg',
    normalized_price_usd: '',
    product_form: 'Producto fresco',
    source_name: '',
    source_url: '',
    observed_at: today(),
    published: true,
})

const form = reactive(initialForm())

const setDepartmentCoordinates = () => {
    const department = departments.find(([name]) => name === form.region)
    if (!department || form.country_code.toUpperCase() !== 'CO') return
    form.latitude = department[1]
    form.longitude = department[2]
}

const resetForm = () => Object.assign(form, initialForm())

const toggleComparisonVisibility = async () => {
    savingVisibility.value = true
    const enabled = !comparisonEnabled.value

    try {
        const response = await axios.patch(`/resource/fruit/${props.fruitId}/regional-comparison`, { enabled })
        comparisonEnabled.value = Boolean(response.data.regional_comparison_enabled)
        notifications.success(comparisonEnabled.value
            ? 'Comparación geográfica habilitada para este producto.'
            : 'Comparación geográfica oculta para este producto.')
    } catch (error) {
        notifications.error('No fue posible cambiar la visibilidad de la comparación geográfica.')
    } finally {
        savingVisibility.value = false
    }
}

const validationMessage = error => {
    const errors = error.response && error.response.data && error.response.data.errors
    if (!errors) return 'No fue posible guardar el precio regional.'
    return Object.values(errors).flat()[0]
}

const save = async () => {
    saving.value = true
    try {
        await axios.post('/resource/regional-price', {
            ...form,
            country_code: form.country_code.toUpperCase(),
        })
        notifications.success('Precio regional guardado.')
        resetForm()
        Inertia.reload({ only: ['regional_prices'] })
    } catch (error) {
        notifications.error(validationMessage(error))
    } finally {
        saving.value = false
    }
}

const setPublished = async (price, published) => {
    try {
        await axios.patch(`/resource/regional-price/${price.id}`, { published })
        notifications.success(published ? 'Precio publicado.' : 'Precio ocultado.')
        Inertia.reload({ only: ['regional_prices'] })
    } catch (error) {
        notifications.error('No fue posible cambiar la publicación.')
    }
}

const remove = price => {
    notifications.confirm('Este registro regional se eliminará de forma permanente.', async () => {
        try {
            await axios.delete(`/resource/regional-price/${price.id}`)
            notifications.success('Precio regional eliminado.')
            Inertia.reload({ only: ['regional_prices'] })
        } catch (error) {
            notifications.error('No fue posible eliminar el precio regional.')
        }
    })
}

const formatUsd = value => new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD', maximumFractionDigits: 4,
}).format(Number(value))

const formatDate = value => value
    ? new Intl.DateTimeFormat('es-CO', { dateStyle: 'medium', timeZone: 'UTC' }).format(new Date(`${String(value).slice(0, 10)}T00:00:00Z`))
    : 'Sin fecha'
</script>

<template>
    <section class="container relative px-4 pt-5 mx-auto">
        <div class="overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
            <header class="p-5 border-b md:p-7 bg-light_yellow border-light_gray">
                <p class="text-sm font-bold tracking-wider uppercase text-enriqueto_green">Precios regionales</p>
                <h2 class="mt-1 text-2xl font-bold text-enriqueto_green">Fuentes comparables por región</h2>
                <p class="max-w-4xl mt-2 text-sm leading-relaxed text-gray">
                    Cada registro conserva su fuente, fecha, moneda y unidad. El valor normalizado en USD permite comparar regiones sin mezclar presentaciones distintas.
                </p>
                <div class="flex flex-col gap-4 p-4 mt-5 bg-white border md:flex-row md:items-center md:justify-between rounded-2xl border-light_gray">
                    <div>
                        <p class="font-bold text-enriqueto_green">Visibilidad en {{ comparisonEnabled ? 'activada' : 'desactivada' }} para este producto</p>
                        <p v-if="!regionalComparisonGloballyEnabled" class="mt-1 text-sm text-gray">El control general está apagado, por lo que la sección permanece oculta aunque este producto esté habilitado.</p>
                        <p v-else class="mt-1 text-sm text-gray">La sección pública se muestra únicamente cuando este control individual también está activado.</p>
                    </div>
                    <button
                        type="button"
                        :disabled="savingVisibility"
                        class="px-5 py-3 font-bold border rounded-xl disabled:opacity-50"
                        :class="comparisonEnabled ? 'border-red text-red bg-white' : 'border-enriqueto_green text-white bg-enriqueto_green'"
                        @click="toggleComparisonVisibility"
                    >
                        {{ savingVisibility ? 'Guardando…' : (comparisonEnabled ? 'Ocultar en este producto' : 'Mostrar en este producto') }}
                    </button>
                </div>
            </header>

            <form class="grid gap-5 p-5 md:grid-cols-2 lg:grid-cols-4 md:p-7" @submit.prevent="save">
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">País</span>
                    <input v-model.trim="form.country" required class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Código del país</span>
                    <input v-model.trim="form.country_code" required maxlength="2" class="w-full px-4 py-3 mt-2 uppercase border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Departamento o región</span>
                    <input v-model.trim="form.region" required list="colombian-departments" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" @change="setDepartmentCoordinates" />
                    <datalist id="colombian-departments">
                        <option v-for="department in departments" :key="department[0]" :value="department[0]" />
                    </datalist>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Municipio o mercado (opcional)</span>
                    <input v-model.trim="form.municipality" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>

                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Latitud</span>
                    <input v-model="form.latitude" required type="number" min="-90" max="90" step="any" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Longitud</span>
                    <input v-model="form.longitude" required type="number" min="-180" max="180" step="any" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Precio de la fuente</span>
                    <input v-model="form.price" required type="number" min="0" step="any" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Moneda original</span>
                    <select v-model="form.currency" required class="w-full px-4 py-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option v-for="currency in currencies" :key="currency" :value="currency">{{ currency }}</option>
                    </select>
                </label>

                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Unidad comparable</span>
                    <select v-model="form.unit" required class="w-full px-4 py-3 mt-2 bg-white border rounded-xl border-light_gray">
                        <option v-for="unit in units" :key="unit[0]" :value="unit[0]">{{ unit[1] }}</option>
                    </select>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Precio normalizado (USD)</span>
                    <input v-model="form.normalized_price_usd" required type="number" min="0" step="any" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                    <span class="block mt-1 text-xs text-gray">Por la misma unidad elegida arriba.</span>
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Presentación del producto</span>
                    <input v-model.trim="form.product_form" required placeholder="Ej. Producto fresco o aceite crudo" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Fecha observada</span>
                    <input v-model="form.observed_at" required type="date" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>

                <label class="block">
                    <span class="text-sm font-bold text-enriqueto_green">Nombre de la fuente</span>
                    <input v-model.trim="form.source_name" required placeholder="Ej. Agronegocios" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="block md:col-span-2">
                    <span class="text-sm font-bold text-enriqueto_green">Enlace de la fuente (opcional)</span>
                    <input v-model.trim="form.source_url" type="url" placeholder="https://…" class="w-full px-4 py-3 mt-2 border rounded-xl border-light_gray" />
                </label>
                <label class="flex items-center self-end gap-3 px-4 py-3 border rounded-xl border-light_gray">
                    <input v-model="form.published" type="checkbox" class="w-5 h-5" />
                    <span class="text-sm font-bold text-enriqueto_green">Visible al público</span>
                </label>

                <div class="flex flex-col gap-3 md:flex-row lg:col-span-4">
                    <button type="submit" :disabled="saving" class="px-6 py-3 font-bold text-white rounded-xl bg-enriqueto_green disabled:opacity-50">
                        {{ saving ? 'Guardando…' : 'Guardar precio regional' }}
                    </button>
                    <button type="button" class="px-6 py-3 font-bold bg-white border rounded-xl border-light_gray text-enriqueto_green" @click="resetForm">Limpiar formulario</button>
                </div>
            </form>

            <div class="p-5 border-t md:p-7 border-light_gray">
                <h3 class="text-lg font-bold text-enriqueto_green">Registros guardados ({{ regionalPrices.length }})</h3>
                <p v-if="!regionalPrices.length" class="py-8 text-sm text-center text-gray">Todavía no hay precios regionales para este producto.</p>
                <div v-else class="grid gap-4 mt-5 lg:grid-cols-2">
                    <article v-for="price in regionalPrices" :key="price.id" class="p-5 border rounded-2xl border-light_gray" :class="price.published ? 'bg-white' : 'bg-light_yellow'">
                        <div class="flex items-start justify-between gap-4">
                            <div>
                                <p class="text-xs font-bold tracking-wider uppercase text-light_green">{{ price.country }} · {{ price.region }}</p>
                                <h4 class="mt-1 text-lg font-bold text-enriqueto_green">{{ formatUsd(price.normalized_price_usd) }} / {{ price.unit }}</h4>
                                <p class="mt-1 text-sm text-gray">{{ price.product_form }} · {{ price.currency }} {{ price.price }}</p>
                            </div>
                            <span class="px-3 py-1 text-xs font-bold rounded-full" :class="price.published ? 'text-white bg-enriqueto_green' : 'text-gray bg-white'">
                                {{ price.published ? 'Público' : 'Oculto' }}
                            </span>
                        </div>
                        <p class="mt-4 text-sm"><strong>{{ price.source_name }}</strong> · {{ formatDate(price.observed_at) }}</p>
                        <a v-if="price.source_url" :href="price.source_url" target="_blank" rel="noopener" class="inline-block mt-2 text-sm underline text-enriqueto_green">Abrir fuente</a>
                        <div class="flex flex-wrap gap-2 mt-5">
                            <button type="button" class="px-4 py-2 text-sm font-bold border rounded-xl border-enriqueto_green text-enriqueto_green" @click="setPublished(price, !price.published)">
                                {{ price.published ? 'Ocultar' : 'Publicar' }}
                            </button>
                            <button type="button" class="px-4 py-2 text-sm font-bold text-white bg-red rounded-xl" @click="remove(price)">Eliminar</button>
                        </div>
                    </article>
                </div>
            </div>
        </div>
    </section>
</template>
